// journal-voucher-list.component.ts
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom, Subscription } from 'rxjs';
import { ListStateService } from '../services/list-state/list-state.service';
import moment from 'moment';
import * as XLSX from 'xlsx';

import { JournalVoucherService } from '../services/journal-voucher/journal-voucher.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { MastersService } from '../services/masters/masters.service';
import {
  DynamicListingComponent,
  FilterConfig,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
  FilterEvent,
} from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { ExportApiService, CreateExportJobRequest } from '../services/export/export-api.service';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ExportDialogComponent } from '../export-dialog/export-dialog.component';
import { openInNewTab } from '../utils/url.utils';

@Component({
  selector: 'lib-journal-voucher-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent, DynamicDialogModule],
  providers: [DialogService],
  templateUrl: './journal-voucher-list.html',
  styleUrls: ['./journal-voucher-list.css'],
})
export class JournalVoucherList implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly listStateService = inject(ListStateService);
  private pageSubscription?: Subscription;

  isLoading = false;
  exportFilename = `journal_vouchers_${new Date().toISOString().slice(0, 10)}`;

  tableData: any[] = [];

  postingDateFrom: Date | null = null;
  postingDateTo: Date | null = null;

  transactionNo = '';
  status = '';

  filterConfigs: FilterConfig[] = [];

  columnConfigs: ColumnConfig[] = [
    { key: 'transaction_no', header: 'Transaction No.', type: 'text' },
    { key: 'posting_date', header: 'Posting Date', type: 'date' },
    { key: 'posting_period', header: 'Posting Period', type: 'text' },
    { key: 'subsidiary', header: 'Subsidiary', type: 'text', ellipsis: true },
    { key: 'memo', header: 'Memo', type: 'text', ellipsis: true },
    { key: 'status', header: 'Status', type: 'text' },
    { key: 'approval_status', header: 'Approval Status', type: 'text' },
    { key: 'total_debit', header: 'Total Debit', type: 'number' },
    { key: 'total_credit', header: 'Total Credit', type: 'number' },
    { key: 'created_at', header: 'Created Date', type: 'date' },
  ];

  actionConfigs: ActionConfig[] = [
    {
      key: 'view',
      label: 'View',
      tooltip: 'View journal voucher',
      severity: 'secondary',
      svgIcon: SVG_ICONS.VIEW,
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit journal voucher',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT,
      show: (row: any) => row.status !== 'reversed',
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete journal voucher',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE,
      show: (row: any) => row.status !== 'reversed',
    },
  ];

  pageIndex = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 0;
  columnPreferenceId?: string;

  constructor(
    private router: Router,
    private journalVoucherService: JournalVoucherService,
    private toasterService: ToasterService,
    private exportApiService: ExportApiService,
    private mastersService: MastersService,
    private dialogService: DialogService
  ) {}

  ngOnInit() {
    this.initializeFilterConfigs();

    // Subscribe to URL page changes
    this.pageSubscription = this.listStateService.getPageFromRoute(this.route).subscribe(page => {
      this.loadJournalVouchers(page);
    });
  }

  ngOnDestroy() {
    this.pageSubscription?.unsubscribe();
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'postingDateFrom',
        type: 'date',
        placeholder: 'Posting Date From',
        value: this.postingDateFrom,
        showInAccordion: true,
      },
      {
        key: 'postingDateTo',
        type: 'date',
        placeholder: 'Posting Date To',
        value: this.postingDateTo,
        showInAccordion: true,
      },
      {
        key: 'transactionNo',
        type: 'text',
        placeholder: 'Transaction No.',
        value: this.transactionNo,
        showInAccordion: true,
      },
      {
        key: 'status',
        type: 'select',
        placeholder: 'Status',
        value: this.status,
        options: [
          { label: 'Draft', value: 'draft' },
          { label: 'Posted', value: 'posted' },
          { label: 'Voided', value: 'voided' },
          { label: 'Reversed', value: 'reversed' },
        ],
        optionLabel: 'label',
        optionValue: 'value',
        showInAccordion: true,
      },
    ];
  }

  async loadJournalVouchers(page: number = 1) {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const filters: any = {
        limit: this.rowsPerPage,
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        is_deleted: false,
        get_references: 'true',
        include_children: 'false',
      };

      if (this.transactionNo?.trim()) {
        filters.transaction_no = this.transactionNo.trim();
      }

      if (this.postingDateFrom) {
        filters.posting_date_from = moment(this.postingDateFrom).format('YYYY-MM-DD');
      }

      if (this.postingDateTo) {
        filters.posting_date_to = moment(this.postingDateTo).format('YYYY-MM-DD');
      }

      if (this.status) {
        filters.status = this.status;
      }

      const response = await lastValueFrom(
        this.journalVoucherService.getJournalVouchers(filters)
      );

      // Extract column preference from response
      if (response?.data?.column_preference) {
        const columnPreference = response.data.column_preference;
        this.columnPreferenceId = columnPreference.id
          ? String(columnPreference.id)
          : undefined;

        // Update columnConfigs with columns_list from preference
        if (
          columnPreference.columns_list &&
          Array.isArray(columnPreference.columns_list)
        ) {
          this.columnConfigs = columnPreference.columns_list.map(
            (col: any) => ({
              key: col.key,
              header: col.header || col.label,
              type: col.type,
              ellipsis: col.ellipsis,
              align: col.align,
              is_selected: col.is_selected !== false,
            })
          );
        }
      }

      const journalVouchers = response?.data?.journal_vouchers ?? [];

      this.tableData = journalVouchers.map((jv: any) => {
        // Calculate totals from lines
        const lines = jv.journal_voucher_lines || [];
        const totalDebit = lines.reduce(
          (sum: number, line: any) => sum + parseFloat(String(line.debit || 0)),
          0
        );
        const totalCredit = lines.reduce(
          (sum: number, line: any) =>
            sum + parseFloat(String(line.credit || 0)),
          0
        );

        return {
          id: jv.id ?? '',
          transaction_no: jv.transaction_no ?? '',
          posting_date: jv.posting_date ?? '',
          posting_period:
            jv.posting_period?.period_name ?? jv.posting_period ?? '',
          subsidiary: jv.subsidiary?.name ?? jv.subsidiary_id ?? '',
          memo: jv.memo ?? '',
          status: jv.status ?? '',
          approval_status: this.formatApprovalStatus(jv.approval_status),
          total_debit: jv.total_debit.toFixed(2),
          total_credit: jv.total_credit.toFixed(2),
          created_at: jv.created_at ?? '',
        };
      });

      this.totalCount = response?.data?.total ?? this.tableData.length;
      this.totalPages = Math.max(
        1,
        Math.ceil(this.totalCount / this.rowsPerPage)
      );
    } catch (error) {
      console.error('Error loading journal vouchers:', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.toasterService.error('Error', 'Failed to load journal vouchers');
    } finally {
      this.isLoading = false;
    }
  }

  onFilterEvent(event: FilterEvent) {
    this.postingDateFrom = event.data?.postingDateFrom ?? null;
    this.postingDateTo = event.data?.postingDateTo ?? null;
    this.transactionNo = event.data?.transactionNo ?? '';
    this.status = event.data?.status ?? '';

    this.listStateService.clearPage();
    this.loadJournalVouchers(1);
  }

  onColumnsSelected(_: string[]) {
    // Future: save column preferences
  }

  onColumnPreferenceSaved(event: { id: string; columns: ColumnConfig[] }) {
    this.columnPreferenceId = event.id ? String(event.id) : undefined;
    this.columnConfigs = event.columns;
  }

  onActionClicked(event: ActionEvent) {
    const journalVoucherId = event.row?.id;
    if (!journalVoucherId) return;

    switch (event.action) {
      case 'view':
        this.view(journalVoucherId);
        break;

      case 'edit':
        this.edit(journalVoucherId);
        break;
      case 'delete':
        this.delete(journalVoucherId);
        break;
    }
  }

  onPageChanged(page: number) {
    this.listStateService.setPage(page);
  }

  // CSV export - opens export dialog for backend export
  exportCsv() {
    this.openExportDialog('csv');
  }

  // XLSX export - opens export dialog for backend export
  exportXlsx() {
    this.openExportDialog('excel');
  }

  /**
   * Opens the export dialog with filter and column options.
   * @param format Default export format
   */
  private openExportDialog(format: 'excel' | 'csv'): void {
    const dialogRef = this.dialogService.open(ExportDialogComponent, {
      header: 'Export Journal Vouchers',
      width: '500px',
      data: {
        module: 'journal_voucher',
        columns: this.columnConfigs,
        filters: this.getCurrentFilters(),
        defaultFormat: format
      }
    });

    if (dialogRef) {
      dialogRef.onClose.subscribe((request: CreateExportJobRequest) => {
        if (request) {
          this.startExport(request);
        }
      });
    }
  }

  /**
   * Gets the current filter values for the export.
   */
  private getCurrentFilters(): Record<string, any> {
    return {
      transaction_no: this.transactionNo.trim() || undefined,
      posting_date_from: this.postingDateFrom
        ? moment(this.postingDateFrom).format('YYYY-MM-DD')
        : undefined,
      posting_date_to: this.postingDateTo
        ? moment(this.postingDateTo).format('YYYY-MM-DD')
        : undefined,
      status: this.status || undefined,
    };
  }

  /**
   * Starts the export job via the API.
   */
  private startExport(request: CreateExportJobRequest): void {
    this.exportApiService.createExportJob(request).subscribe({
      next: () => {
        this.toasterService.success(
          'Export Started',
          'Your export is processing. Click here to view progress →',
          5000
        );
        // Auto-open exports page in new tab after a short delay
        setTimeout(() => openInNewTab('/exports'), 1500);
      },
      error: (err: any) => {
        const message = err?.error?.message || 'Failed to start export';
        this.toasterService.error('Export Failed', message);
      }
    });
  }

  // PDF export
  exportPdf() {
    window.print();
  }

  view(journalVoucherId: string | number) {
  if (!journalVoucherId) return;

  this.mastersService.openInNewTab(
    ['/finance/journal-vouchers', String(journalVoucherId), 'view']
  );
}

edit(journalVoucherId: string | number) {
  if (!journalVoucherId) return;

  this.mastersService.openInNewTab(
    ['/finance/journal-vouchers', String(journalVoucherId), 'edit']
  );
}

  async delete(journalVoucherId: string | number) {
    if (!confirm('Are you sure you want to delete this journal voucher?'))
      return;

    try {
      this.isLoading = true;
      await lastValueFrom(
        this.journalVoucherService.deleteJournalVoucher(journalVoucherId)
      );
      this.toasterService.success(
        'Success',
        'Journal voucher deleted successfully'
      );
      await this.loadJournalVouchers(this.pageIndex);
    } catch (error: any) {
      const message =
        error?.error?.message ||
        error?.message ||
        'Failed to delete journal voucher';
      this.toasterService.error('Error', message);
    } finally {
      this.isLoading = false;
    }
  }

  // Helper method to format approval status for display
  formatApprovalStatus(status: string | null | undefined): string {
    const labels: { [key: string]: string } = {
      pending: 'Pending Approval',
      approved: 'Approved',
      rejected: 'Rejected',
      not_required: 'No Approval Required',
    };
    return labels[status || 'not_required'] || status || 'No Approval Required';
  }

}
